import { type ChangeEvent, type KeyboardEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsomorphicLayoutEffect } from 'react-use';

import { permissionsSelector } from '@air/redux-permissions';

import { PrivateWorkspaceAgentSelectAssetBoardListQueries } from '~/components/v2/components/workspace/workspace-agent-panel/components/select-asset/PrivateWorkspaceAgentSelectAssetBoardListQueries';
import { useWorkspaceAgentComposerWorkspaceId } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/WorkspaceAgentComposerWorkspaceProvider';
import { getWorkspaceAgentSelectAssetEmptyCopy } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetEmptyCopy';
import { getWorkspaceAgentSelectAssetActiveOptionId } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetOptionId';
import { WorkspaceAgentSelectAssetSearchInput } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetSearchInput';
import { WorkspaceAgentSelectAssetVirtualList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetVirtualList';
import { type WorkspaceAgentSelectAssetResults } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { usePrivateWorkspaceAgentBoardTree } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/usePrivateWorkspaceAgentBoardTree';
import { useWorkspaceAgentMentionChildPrefetch } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionChildPrefetch';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import {
  getWorkspaceAgentGuidedVariantChooseBoardExpandedNodeIds,
  getWorkspaceAgentGuidedVariantChooseBoardPendingListNodeId,
  resolveWorkspaceAgentGuidedVariantChooseBoardHighlightIndex,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantChooseBoard';
import { type WorkspaceAgentGuidedVariantChosenBoard } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { centralizedBoardIdLibraryAncestorsSelector } from '~/store/centralizedBoard/selectors';
import { canAddAssetsToBoard } from '~/utils/permissions/boardPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const NO_INITIAL_EXPANDED_NODE_IDS: readonly string[] = [];

const EMPTY_NODE_IDS: ReadonlySet<string> = new Set();

const noop = () => undefined;

type ChooseBoardPagedListAttempt = {
  itemCount: number;
  nodeId: string;
};

export type PrivateWorkspaceAgentGuidedVariantChooseBoardMenuProps = {
  onSelectBoard: (board: WorkspaceAgentGuidedVariantChosenBoard) => void;
};

const boardIdsInResults = (items: WorkspaceAgentSelectAssetResults['items']) =>
  items.flatMap((item) => (isWorkspaceAgentMentionTreeItem(item) && item.kind === 'board' ? [item.boardId] : []));

export const PrivateWorkspaceAgentGuidedVariantChooseBoardMenu = memo(
  ({ onSelectBoard }: PrivateWorkspaceAgentGuidedVariantChooseBoardMenuProps) => {
    const workspaceId = useWorkspaceAgentComposerWorkspaceId();
    const currentBoard = useSelector(centralizedBoardIdLibraryAncestorsSelector);
    const { getBoardPermissions } = useBoardPermissionsCache();
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [isKeyboardHighlight, setIsKeyboardHighlight] = useState(false);
    const [hasUserMovedHighlight, setHasUserMovedHighlight] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const previousItemsRef = useRef<WorkspaceAgentSelectAssetResults['items']>([]);
    const lastPagedListRef = useRef<ChooseBoardPagedListAttempt | null>(null);

    const initialExpandedNodeIds = useMemo(
      () =>
        currentBoard
          ? getWorkspaceAgentGuidedVariantChooseBoardExpandedNodeIds(currentBoard)
          : NO_INITIAL_EXPANDED_NODE_IDS,
      [currentBoard],
    );

    const boardTree = usePrivateWorkspaceAgentBoardTree({
      enabled: true,
      initialExpandedNodeIds,
      isPickerOpen: true,
      query,
      workspaceId,
    });

    const { prefetchSpec } = useWorkspaceAgentMentionChildPrefetch({
      highlightedIndex,
      items: boardTree.items,
      listQuerySpecs: boardTree.listQuerySpecs,
      onToggleExpanded: boardTree.toggleExpanded,
      query,
      workspaceId,
    });

    const listQuerySpecs =
      prefetchSpec && boardTree.listQuerySpecs.every((spec) => spec.nodeId !== prefetchSpec.nodeId)
        ? [...boardTree.listQuerySpecs, prefetchSpec]
        : boardTree.listQuerySpecs;

    const boardIds = useMemo(() => boardIdsInResults(boardTree.items), [boardTree.items]);

    useFetchObjectsPermissions({
      objects: { boardIds },
    });

    const boardSavePermissionKey = useAirSelector((state) =>
      boardIds.map((boardId) => permissionsSelector(state, 'boardIds', boardId)?.board.canEdit ?? 'pending').join(','),
    );

    const boardDisabledById = useMemo(() => {
      const canEditByIndex = boardSavePermissionKey.split(',');

      return Object.fromEntries(
        boardIds.map((boardId, index) => {
          if (canEditByIndex[index] === 'pending') {
            return [boardId, true];
          }

          return [boardId, !canAddAssetsToBoard(getBoardPermissions(boardId))];
        }),
      );
    }, [boardIds, boardSavePermissionKey, getBoardPermissions]);

    const results = useMemo(
      (): WorkspaceAgentSelectAssetResults => ({
        hasNextPage: false,
        hasNextPageNodeIds: boardTree.hasNextPageNodeIds,
        isError: boardTree.isError,
        isFetching: boardTree.isFetching,
        isLoading: boardTree.isLoading,
        isStale: boardTree.isStale,
        items: boardTree.items,
        onFetchNextPage: noop,
        onFetchNextPageForNode: boardTree.fetchNextPageForNode,
        onRetry: boardTree.refetch,
        onToggleExpanded: boardTree.toggleExpanded,
        pendingChildCountNodeIds: boardTree.pendingChildCountNodeIds ?? EMPTY_NODE_IDS,
        getIsBoardItemDisabled: (boardId: string) => boardDisabledById[boardId] ?? true,
      }),
      [
        boardTree.fetchNextPageForNode,
        boardTree.hasNextPageNodeIds,
        boardTree.isError,
        boardTree.isFetching,
        boardTree.isLoading,
        boardTree.isStale,
        boardTree.items,
        boardTree.pendingChildCountNodeIds,
        boardTree.refetch,
        boardTree.toggleExpanded,
        boardDisabledById,
      ],
    );

    useIsomorphicLayoutEffect(() => {
      setHighlightedIndex((previousIndex) =>
        resolveWorkspaceAgentGuidedVariantChooseBoardHighlightIndex({
          currentBoardId: currentBoard?.id,
          hasUserMovedHighlight,
          nextItems: boardTree.items,
          previousIndex,
          previousItems: previousItemsRef.current,
          query,
        }),
      );
      previousItemsRef.current = boardTree.items;
    }, [boardTree.items, currentBoard?.id, hasUserMovedHighlight, query]);

    // The list pages when its tail is on screen. An ancestor (or the current board) can sit on a
    // later page of a closed parent, so keep asking the first missing list until that row is present.
    // A failed next page must not retry until the list grows (or the parent changes).
    useEffect(() => {
      if (!currentBoard || query !== '') {
        lastPagedListRef.current = null;
        return;
      }

      const listNodeId = getWorkspaceAgentGuidedVariantChooseBoardPendingListNodeId(currentBoard, boardTree.items);

      if (!listNodeId || !boardTree.hasNextPageNodeIds.has(listNodeId)) {
        return;
      }

      const lastPagedList = lastPagedListRef.current;

      if (lastPagedList?.nodeId === listNodeId && lastPagedList.itemCount === boardTree.items.length) {
        return;
      }

      lastPagedListRef.current = { itemCount: boardTree.items.length, nodeId: listNodeId };
      boardTree.fetchNextPageForNode(listNodeId);
    }, [boardTree.fetchNextPageForNode, boardTree.hasNextPageNodeIds, boardTree.items, currentBoard, query]);

    const selectItem = useCallback(
      (itemId: string) => {
        const item = boardTree.items.find((row) => row.id === itemId);

        if (!item || !isWorkspaceAgentMentionTreeItem(item) || boardTree.isStale) {
          return;
        }

        if (item.kind === 'library') {
          boardTree.toggleExpanded(item.id, item.isExpanded);
          return;
        }

        if (!canAddAssetsToBoard(getBoardPermissions(item.boardId))) {
          return;
        }

        onSelectBoard({ id: item.boardId, title: item.title || 'Untitled board' });
      },
      [boardTree, getBoardPermissions, onSelectBoard],
    );

    const handleSelectItem = useCallback(
      (itemId: string) => {
        selectItem(itemId);
      },
      [selectItem],
    );

    const handleHighlight = useCallback(
      (index: number) => {
        if (index !== highlightedIndex) {
          setHasUserMovedHighlight(true);
        }

        setHighlightedIndex(index);
        setIsKeyboardHighlight(false);
      },
      [highlightedIndex],
    );

    const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    }, []);

    const handleClearQuery = useCallback(() => {
      setQuery('');
    }, []);

    const handleSearchKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        const optionCount = boardTree.items.length;
        const currentIndex = optionCount > 0 ? Math.min(highlightedIndex, optionCount - 1) : 0;

        if (event.key === 'ArrowDown' && optionCount > 0) {
          event.preventDefault();
          setHasUserMovedHighlight(true);
          setIsKeyboardHighlight(true);
          setHighlightedIndex((currentIndex + 1) % optionCount);
          return;
        }

        if (event.key === 'ArrowUp' && optionCount > 0) {
          event.preventDefault();
          setHasUserMovedHighlight(true);
          setIsKeyboardHighlight(true);
          setHighlightedIndex((currentIndex - 1 + optionCount) % optionCount);
          return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          const item = boardTree.items[currentIndex];
          const shouldExpand = event.key === 'ArrowRight';

          if (
            item &&
            isWorkspaceAgentMentionTreeItem(item) &&
            item.hasChildren &&
            item.isExpanded !== shouldExpand &&
            boardTree.toggleExpanded
          ) {
            event.preventDefault();
            boardTree.toggleExpanded(item.id, item.isExpanded);
          }

          return;
        }

        if (event.key !== 'Enter') {
          return;
        }

        event.preventDefault();

        if (boardTree.isError && optionCount === 0) {
          boardTree.refetch();
          return;
        }

        const highlightedItem = boardTree.items[currentIndex];

        if (highlightedItem) {
          selectItem(highlightedItem.id);
        }
      },
      [boardTree, highlightedIndex, selectItem],
    );

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const emptyStateTitle = getWorkspaceAgentSelectAssetEmptyCopy({
      hasFailed: boardTree.isError && boardTree.items.length === 0,
      isBoardPickerOpen: true,
      query,
      source: 'board',
    });
    const activeOptionId = getWorkspaceAgentSelectAssetActiveOptionId(boardTree.items, highlightedIndex);
    const highlightedItem = boardTree.items[highlightedIndex];
    const scrollHighlightedIntoView =
      !hasUserMovedHighlight &&
      query === '' &&
      !!currentBoard &&
      !!highlightedItem &&
      isWorkspaceAgentMentionTreeItem(highlightedItem) &&
      highlightedItem.kind === 'board' &&
      highlightedItem.boardId === currentBoard.id;

    return (
      <div className="flex h-full min-h-0 flex-col gap-2" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CHOOSE_BOARD">
        <div
          className="shrink-0 px-1 ads:text-metadata text-12 text-gray-10"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CHOOSE_BOARD_TITLE"
        >
          Choose board
        </div>
        <WorkspaceAgentSelectAssetSearchInput
          activeOptionId={activeOptionId}
          inputRef={inputRef}
          onChange={handleQueryChange}
          onClear={handleClearQuery}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search workspace"
          query={query}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <WorkspaceAgentSelectAssetVirtualList
            activeOptionId={activeOptionId}
            ariaLabel="Choose board"
            emptyStateTitle={emptyStateTitle}
            highlightedIndex={highlightedIndex}
            isKeyboardHighlight={isKeyboardHighlight}
            listPaginationKey={`choose-board|${query}`}
            listRef={listRef}
            onHighlight={handleHighlight}
            onSelectItem={handleSelectItem}
            scrollHighlightedIntoView={scrollHighlightedIntoView}
            results={{
              ...results,
              boardListQueries: (
                <PrivateWorkspaceAgentSelectAssetBoardListQueries
                  onSnapshot={boardTree.onListSnapshot}
                  specs={listQuerySpecs}
                />
              ),
            }}
            role="tree"
          />
        </div>
      </div>
    );
  },
);

PrivateWorkspaceAgentGuidedVariantChooseBoardMenu.displayName = 'PrivateWorkspaceAgentGuidedVariantChooseBoardMenu';
